import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  isAuthenticatedSelector,
  setAuthTokens,
} from '@/store/auth/authSlices';
import { Form, Formik, getIn } from 'formik';
import Input from '@/components/shared/Input';
import Button from '@/components/shared/Button';
import TermsAndConditionsModal from '@/components/shared/TermsAndConditionsModal';
import { fetchProfileSuccess } from '@/store/profile/profileSlices';
import { getUserEmailByInviteToken, signupRequest } from '@/api';
import { useToast } from '@/components/shared/ToastProvider';
import logo from '@/img/propsavvy-logo-with-text.svg';
import * as Yup from 'yup';
import getAxiosErrors from '@/utils/getAxiosErrors';
import './SignupTenant.scss';
import { SignupDto, UserRole } from '@/types';

const baseClass = 'SignupTenant';

interface IValues {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

const initialVal: IValues = {
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  confirmPassword: '',
  agreeToTerms: false,
};

const validationSchema = Yup.object().shape({
  firstname: Yup.string().required('First name is required').min(2),
  lastname: Yup.string().required('Last name is required').min(2),
  email: Yup.string().required('Email is required').email(),
  password: Yup.string()
    .required('Password is required')
    .min(7, 'Password must be at least 7 characters')
    .max(128, 'Password must be at most 128 characters')
    .matches(
      /(?=.*[A-Z])/,
      'Password must contain at least one uppercase letter',
    )
    .matches(
      /(?=.*\d)|(?=.*\W+)/,
      'Password must contain at least one number or special character',
    ),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
  agreeToTerms: Yup.boolean()
    .oneOf([true], 'You must agree to the terms and conditions')
    .required('You must agree to the terms and conditions'),
});

const SignupTenant: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('inviteToken');
  const [initialValues, setInitialValues] = useState<IValues>(initialVal);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'terms' | 'privacy'>('terms');
  const [errorShown, setErrorShown] = useState(false);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (!inviteToken) {
      console.warn('SignupTenant: Missing inviteToken, redirecting to login');
      showError('Missing invite token. Redirecting to login...');
      setTimeout(() => navigate('/login'), 1500);
      return;
    }

    setIsEmailLoading(true);
    getUserEmailByInviteToken({ inviteToken })
      .then((res) => {
        if (res.email) {
          setInitialValues((prev) => ({ ...prev, email: res.email }));
        }
      })
      .catch((err) => {
        console.log('getUserEmailByInviteToken error:', getAxiosErrors(err));
        if (!errorShown) {
          showError('Failed to load invitation details');
          setErrorShown(true);
        }
      })
      .finally(() => {
        setIsEmailLoading(false);
      });
  }, [inviteToken, navigate, showError, errorShown]);

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setModalType('terms');
    setShowModal(true);
  };

  const handlePrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setModalType('privacy');
    setShowModal(true);
  };

  const handleAccept = (
    setFieldValue: (field: string, value: any) => void,
  ) => {
    setFieldValue('agreeToTerms', true);
    setShowModal(false);
  };

  return (
    <div className={baseClass}>
      <div className={`${baseClass}__view`}>
        <div className={`${baseClass}__logo`}>
          <img src={logo} alt="logo" />
          <p className={`${baseClass}__logo-desc`}>
            Please enter your user information.
          </p>
        </div>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, setErrors }) => {
            if (!inviteToken) {
              setErrors({ email: 'Missing invite token' });
              setSubmitting(false);
              return;
            }

            const data: SignupDto = {
              role: UserRole.TENANT,
              firstname: values.firstname,
              lastname: values.lastname,
              email: values.email,
              password: values.password,
              inviteToken: inviteToken,
            };

            signupRequest(data)
              .then((res) => {
                console.debug('SignupTenant success:', res.data);
                const { accessToken, user } = res.data;

                dispatch(setAuthTokens(accessToken));
                dispatch(fetchProfileSuccess(user));

                showSuccess('Registration successful! Redirecting...');

                setTimeout(() => {
                  if (inviteToken) {
                    navigate(`/invite/${inviteToken}`);
                  } else {
                    navigate('/');
                  }
                }, 1500);
              })
              .catch((err) => {
                console.debug('SignupTenant error:', getAxiosErrors(err));

                const errorData = getAxiosErrors(err);
                const message =
                  errorData.message ||
                  err.response?.data?.message ||
                  'Registration failed. Please try again.';

                showError(message);

                if (errorData.errors || err.response?.data?.errors) {
                  setErrors(errorData.errors || err.response.data.errors);
                }
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({
            values,
            errors,
            handleBlur,
            handleChange,
            touched,
            isSubmitting,
            setFieldValue,
          }) => {
            return (
              <>
                <Form className={`${baseClass}__form`}>
                  <div className={`${baseClass}__form-group`}>
                    <Input
                      floatLabel
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.firstname}
                      name="firstname"
                      type="text"
                      label="First name"
                      errors={
                        getIn(errors, 'firstname') &&
                        getIn(touched, 'firstname')
                          ? getIn(errors, 'firstname')
                          : ''
                      }
                    />

                    <Input
                      floatLabel
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.lastname}
                      name="lastname"
                      type="text"
                      label="Last name"
                      errors={
                        getIn(errors, 'lastname') && getIn(touched, 'lastname')
                          ? getIn(errors, 'lastname')
                          : ''
                      }
                    />

                    <Input
                      floatLabel
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      isLoading={isEmailLoading}
                      isDisabled={isEmailLoading}
                      name="email"
                      type="text"
                      label="E-mail"
                      errors={
                        getIn(errors, 'email') && getIn(touched, 'email')
                          ? getIn(errors, 'email')
                          : ''
                      }
                    />

                    <Input
                      floatLabel
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      name="password"
                      type="password"
                      label="Password"
                      errors={
                        getIn(errors, 'password') && getIn(touched, 'password')
                          ? getIn(errors, 'password')
                          : ''
                      }
                    />

                    <Input
                      floatLabel
                      className={`${baseClass}__form-group-password`}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.confirmPassword}
                      name="confirmPassword"
                      type="password"
                      label="Confirm password"
                      errors={
                        getIn(errors, 'confirmPassword') &&
                        getIn(touched, 'confirmPassword')
                          ? getIn(errors, 'confirmPassword')
                          : ''
                      }
                    />

                    <div className={`${baseClass}__terms`}>
                      <label className={`${baseClass}__terms-label`}>
                        <input
                          type="checkbox"
                          name="agreeToTerms"
                          checked={values.agreeToTerms}
                          onChange={handleChange}
                          className={`${baseClass}__terms-checkbox`}
                        />
                        <span className={`${baseClass}__terms-text`}>
                          By creating an account you agree to the{' '}
                          <a
                            href="#"
                            onClick={handleTermsClick}
                            className={`${baseClass}__terms-link`}
                          >
                            Terms of Service
                          </a>
                          {' and '}
                          <a
                            href="#"
                            onClick={handlePrivacyClick}
                            className={`${baseClass}__terms-link`}
                          >
                            Privacy Policy
                          </a>.
                        </span>
                      </label>
                      {getIn(errors, 'agreeToTerms') &&
                        getIn(touched, 'agreeToTerms') && (
                          <div className={`${baseClass}__field-error`}>
                            {getIn(errors, 'agreeToTerms')}
                          </div>
                        )}
                    </div>

                    <Button
                      isDisabled={isSubmitting || !values.agreeToTerms}
                      type="submit"
                      className={`${baseClass}__submit-btn`}
                      isLoading={isSubmitting}
                    >
                      {isSubmitting ? 'Registering...' : 'Register'}
                    </Button>
                  </div>

                  <div className={`${baseClass}__bottom-links`}>
                    <Link
                      to={`/login?inviteToken=${inviteToken}`}
                      className={`${baseClass}__bottom-links-signin`}
                    >
                      Already member? Sign in
                    </Link>
                    <Link
                      to={'/forgot'}
                      className={`${baseClass}__bottom-links-forgot`}
                    >
                      Forgot password?
                    </Link>
                  </div>
                </Form>

                <TermsAndConditionsModal
                  isOpen={showModal}
                  type={modalType}
                  onClose={() => setShowModal(false)}
                  onAccept={() => handleAccept(setFieldValue)}
                />
              </>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default SignupTenant;